/* eslint-disable max-classes-per-file */
import { camelToKebab } from '@scripts/utils/string-utils';
import { isNumber, isObject, isString } from '@scripts/utils/type-utils';
import { getObjectValue, removeItemByKey } from '@scripts/utils/object-utils';

export type StyleObjectValueType = object | number | string | null;
export type StyleObjectType = Record<string, StyleObjectValueType>;

export const DEFAULT_CSS_PROPERTY_NAMES: ReadonlyArray<string> = Object.freeze([
  'backgroundColor',
  'border',
  'borderRadius',
  'borderWidth',
  'boxShadow',
  'color',
  'cursor',
  'fontFamily',
  'fontSize',
  'fontWeight',
  'textDecoration',
  // 'filter',
  // 'userSelect',
  'outline',
  'outlineColor',
  // 'outlineOffset',
  'outlineWidth',
]);

export const DEFAULT_CSS_VARIABLE_POSTFIXES : ReadonlyArray<string> = Object.freeze([
  '',
  'hover',
  'active',
  'disabled',
]);

export class StyleExtender {
  propertyNames: Array<string>;
  variablePrefixInKebab: string;
  variablePostfixesInKebab: Array<string>;
  propertyNamesInKebab: Array<string>;

  constructor({
    propertyNames = [...DEFAULT_CSS_PROPERTY_NAMES],
    extraPropertyNames = [],
    variablePrefix,
    variablePostfixes = [...DEFAULT_CSS_VARIABLE_POSTFIXES],
    extraVariablePostfixes = [],
  }: {
    propertyNames: Array<string>,
    extraPropertyNames: Array<string>,
    variablePrefix: string,
    variablePostfixes: Array<string>,
    extraVariablePostfixes: Array<string>,
  }) {
    this.propertyNames = Array.from(new Set([
      ...propertyNames, ...extraPropertyNames,
    ]));
    this.propertyNamesInKebab = this.propertyNames.map((name) => camelToKebab({ target: name }));
    this.variablePrefixInKebab = camelToKebab({ target: variablePrefix }).replace(/-*$/, '');
    this.variablePostfixesInKebab = Array.from(new Set([
      ...variablePostfixes, ...extraVariablePostfixes,
    ])).map((postfix) => camelToKebab({ target: postfix }).replace(/^-*/, ''));
  }

  extend({
    target,
    extension,
    selector,
  }: {
    target: StyleObjectType,
    extension: StyleObjectType,
    selector: string,
  }): void {
    if (!isObject(extension)) return;

    Object.keys(extension).forEach((key) => {
      if (key in target) {
        removeItemByKey({ target, key });
      }
    });

    Object.assign(
      target,
      this.getExtraStyleFromExtension(extension),
      this.getExtendedStyleFromExtension({ extension, selector }),
    );
  }

  getExtraStyleFromExtension(extension: StyleObjectType): StyleObjectType {
    const extraPropertyNames = Object.keys(extension).filter(
      (propertyNameInExtension) => !this.propertyNames.includes(propertyNameInExtension),
    );

    return extraPropertyNames.reduce((result, propertyName) => Object.assign(
      result,
      { [propertyName]: extension[propertyName] },
    ), {});
  }

  /*
   e.g.)
   variablePrefixInKebab: '--var'
   selector in kebab: 'base-button'
   propertyNamesInKebab: ['background-color', 'border', 'border-width']
   variablePostfixesInKebab: ['', 'hover', 'border', 'active', 'disabled']
   extension: {
     backgroundColor: {
       '': 'blue',
       disabled: 'gray',
     },
     border: 'solid black',
     borderWidth: '1px',
   }
   Output: {
     '--var-base-button-background-color': 'blue',
     '--var-base-button-background-color-hover': 'blue',
     border: 'solid black',
     borderWidth: '1px',
   }
  */
  getExtendedStyleFromExtension({
    extension,
    selector,
  }: {
    extension: StyleObjectType,
    selector: string,
  }): StyleObjectType {
    const prefix = `${this.variablePrefixInKebab}-${camelToKebab({ target: selector })}`;

    return this.propertyNames.reduce((result, propertyNameInCamel, index) => {
      const property = getObjectValue({
        obj: extension, key: propertyNameInCamel, defaultValue: null as StyleObjectValueType,
      }) as StyleObjectType;
      if (property === null || property === undefined) return result;

      return Object.assign(
        result,
        this.variablePostfixesInKebab.reduce((innerResult, postfix) => {
          // If the property is not object, it's defined normally like
          // key-value is <property-name>-<property-value>.
          // e.g. key: backgroundColor, value: 'rgba(255, 255, 255, 1.0)',

          if (isString(property) || isNumber(property)) {
            return Object.assign(innerResult, { [propertyNameInCamel]: property });
          }

          // If the property is object, css property variables are defined.
          // key-value is <css variable name>-<property-value>
          // const propertyByPostfix = postfix in property
          const propertyByPostfix = postfix in property
            ? property[postfix]
            : property[''];

          const cssVariableName = (
            `${prefix}-${this.propertyNamesInKebab[index]}${postfix ? '-' : ''}${postfix}`
          );

          return Object.assign(
            innerResult,
            {
              [cssVariableName]: propertyByPostfix,
            },
          );
        }, {}),
      );
    }, {});
  }
}
